import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { calculateItemStats } from '../utils/item';
import { statAsString, statsColumnsMapping } from '../utils/stats';

const ItemEditor = React.memo(({ item, baseCategory, prefixesStats, suffixesStats, onUpdate }) => {
    const [localItem, setLocalItem] = useState(item || {
        prefixId: null,
        base: null,
        suffixId: null,
        quality: 0,
        bonus: null,
        bonusValue: 0,
        stats: {}
    });

    // Update local state when props change
    useEffect(() => {
        setLocalItem(item || {
            prefixId: null,
            base: null,
            suffixId: null,
            quality: 0,
            bonus: null,
            bonusValue: 0,
            stats: {}
        });
    }, [item]);

    // Memoize base stats filtering
    const filteredBases = useMemo(() => {
        const baseStats = window.charPlannerData.baseStats;
        if (!baseStats || !baseCategory) return [];
        
        const categoryStats = baseStats[baseCategory];
        if (!categoryStats) return [];
        
        // Get all bases for this category
        return Object.keys(categoryStats).sort((a, b) => {
            const aNum = parseInt(a.split('-')[1]);
            const bNum = parseInt(b.split('-')[1]);
            return aNum - bNum;
        });
    }, [baseCategory]);

    // Memoize item name building
    const itemName = useMemo(() => {
        if (!localItem.base) return '';
        
        const translations = window.charPlannerData.i18n;
        const prefixKey = `prefix-${localItem.prefixId}`;
        const baseKey = `item-${localItem.base}`;
        const suffixKey = `suffix-${localItem.suffixId}`;
        
        const prefix = translations[prefixKey] || '';
        const base = translations[baseKey] || '';
        const suffix = translations[suffixKey] || '';
        
        return `${prefix} ${base} ${suffix}`.trim();
    }, [localItem.prefixId, localItem.base, localItem.suffixId]);

    // Memoize item stats calculation
    const calculatedStats = useMemo(() => {
        if (!localItem.base) return null;
        return calculateItemStats(localItem);
    }, [localItem]);

    // Memoize handlers
    const handlePrefixChange = useCallback((e) => {
        const newPrefixId = e.target.value ? parseInt(e.target.value) : null;
        setLocalItem(prev => ({ ...prev, prefixId: newPrefixId }));
    }, []);

    const handleBaseChange = useCallback((e) => {
        const newBase = e.target.value || null;
        setLocalItem(prev => ({ ...prev, base: newBase }));
    }, []);

    const handleSuffixChange = useCallback((e) => {
        const newSuffixId = e.target.value ? parseInt(e.target.value) : null;
        setLocalItem(prev => ({ ...prev, suffixId: newSuffixId }));
    }, []);

    const handleQualityChange = useCallback((e) => {
        const newQuality = parseInt(e.target.value) || 0;
        setLocalItem(prev => ({ ...prev, quality: newQuality }));
    }, []);

    const handleBonusChange = useCallback((e) => {
        const newBonus = e.target.value || null;
        setLocalItem(prev => ({ ...prev, bonus: newBonus }));
    }, []);

    const handleBonusValueChange = useCallback((e) => {
        const newBonusValue = parseInt(e.target.value) || 0;
        setLocalItem(prev => ({ ...prev, bonusValue: newBonusValue }));
    }, []);

    const handleSave = useCallback(() => {
        if (calculatedStats) {
            onUpdate({
                ...localItem,
                stats: calculatedStats.stats,
                isWeapon: calculatedStats.isWeapon,
                isArmor: calculatedStats.isArmor
            });
        }
    }, [localItem, calculatedStats, onUpdate]);

    // Memoize option rendering
    const renderPrefixOption = useCallback((prefix) => (
        <option key={prefix.id} value={prefix.id}>
            {window.charPlannerData.i18n[`prefix-${prefix.id}`]}
        </option>
    ), []);

    const renderBaseOption = useCallback((base) => (
        <option key={base} value={base}>
            {window.charPlannerData.i18n[`item-${base}`]}
        </option>
    ), []);

    const renderSuffixOption = useCallback((suffix) => (
        <option key={suffix.id} value={suffix.id}>
            {window.charPlannerData.i18n[`suffix-${suffix.id}`] || suffix.name || `Suffix ${suffix.id}`}
        </option>
    ), []);

    const qualityColors = {
        5: "#ff0000", 4: "#ff0000", 3: "#ff6a00", 2: "#e303e0", 1: "#5159f7", 0: "#00b700",
    };

    // Debug base items
    console.log('baseCategory:', baseCategory);
    console.log('baseStats:', window.charPlannerData.baseStats);

    // Debug filtered bases
    console.log('Filtered bases:', filteredBases);

    useEffect(() => {
        console.log('window.charPlannerData:', window.charPlannerData);
        console.log('Current base:', localItem.base);
        console.log('Current prefixId:', localItem.prefixId);
        console.log('Current suffixId:', localItem.suffixId);
        console.log('Available prefixes:', prefixesStats);
        console.log('Available suffixes:', suffixesStats);
    }, [localItem.prefixId, localItem.base, localItem.suffixId, prefixesStats, suffixesStats]);

    useEffect(() => {
        if (localItem.base) {
            updateItemData();
        }
    }, [localItem.prefixId, localItem.base, localItem.suffixId, localItem.quality]);

    const updateItemData = () => {
        if (!localItem.base) return;
        console.log('Updating item data with:', {
            prefixId: localItem.prefixId,
            base: localItem.base,
            suffixId: localItem.suffixId,
            quality: localItem.quality,
            prefixStats: prefixesStats.find(p => p.id === localItem.prefixId)?.stats,
            suffixStats: suffixesStats.find(s => s.id === localItem.suffixId)?.stats,
            baseStats: window.charPlannerData.baseStats[localItem.base]
        });
        const newItemData = calculateItemStats(
            localItem.prefixId,
            localItem.base,
            localItem.suffixId,
            localItem.quality
        );
        console.log('Calculated item data:', newItemData);
        if (onUpdate) {
            onUpdate({
                ...localItem,
                stats: newItemData.stats || {},
                isWeapon: newItemData.isWeapon,
                isArmor: newItemData.isArmor,
                damageLow: newItemData.damageLow,
                damageHigh: newItemData.damageHigh
            });
        }
    };

    const handleCancel = () => {
        setLocalItem({
            prefixId: item.prefixId,
            base: item.base,
            suffixId: item.suffixId,
            quality: item.quality ?? 0,
            bonus: null,
            bonusValue: 0,
            stats: {}
        });
    };

    const stripPercentageFromName = (stat) => {
        return stat.match(/Percentage$/) ? stat.replace("Percentage", "") : stat;
    };

    if (!localItem.base) {
        return (
            <div className="bg-gray-100 py-3 px-4 rounded-sm w-full lg:w-1/3 lg:min-h-16">
                <div 
                    className="font-bold cursor-pointer flex items-center" 
                    onClick={() => setLocalItem({ ...item, base: null })} 
                    role="button"
                >
                    <span className="text-gray-500 mr-2" style={{ display: localItem.base === null ? 'inline' : 'none' }}>
                        {window.charPlannerData.i18n[`base-category-${baseCategory}`] || `Category ${baseCategory}`}:
                    </span>
                    <div 
                        className="icon-edit w-4 h-4 min-w-4 inline-block mr-1" 
                        style={{ background: localItem.base !== null ? qualityColors[localItem.quality] : 'rgb(107, 114, 128)' }}
                    />
                    <span style={{ color: localItem.base !== null ? qualityColors[localItem.quality] : 'inherit' }}>
                        {itemName || 'Empty slot'}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-100 py-3 px-4 rounded-sm w-full lg:w-1/3 lg:min-h-16">
            <div className="flex flex-col gap-y-2">
                <select 
                    value={localItem.prefixId || ''} 
                    className="bg-gray-200 rounded" 
                    onChange={handlePrefixChange}
                >
                    <option value="">{window.charPlannerData.i18n['No prefix'] || 'No prefix'}</option>
                    {prefixesStats.map(renderPrefixOption)}
                </select>

                <div className="flex gap-2">
                    <select 
                        value={localItem.quality} 
                        className="bg-gray-200 rounded w-1/2" 
                        style={{ color: qualityColors[localItem.quality] }}
                        onChange={handleQualityChange}
                    >
                        <option value={5}>Olimp +</option>
                        <option value={4}>Olimp / Jupiter+</option>
                        <option value={3}>Jupiter / Mars+</option>
                        <option value={2}>Mars / Neptun +</option>
                        <option value={1}>Neptun / Ceres +</option>
                        <option value={0}>Ceres</option>
                    </select>

                    <select 
                        value={localItem.base || ''} 
                        className="bg-gray-200 rounded w-1/2" 
                        onChange={handleBaseChange}
                    >
                        <option value="">{window.charPlannerData.i18n['No item'] || 'No item'}</option>
                        {filteredBases.map(renderBaseOption)}
                    </select>
                </div>

                <select 
                    value={localItem.suffixId || ''} 
                    className="bg-gray-200 rounded" 
                    onChange={handleSuffixChange}
                >
                    <option value="">{window.charPlannerData.i18n['No suffix'] || 'No suffix'}</option>
                    {suffixesStats.map(renderSuffixOption)}
                </select>

                <div className="flex w-full gap-2">
                    <select 
                        value={localItem.bonus || ''} 
                        className="bg-gray-200 rounded w-2/3"
                        onChange={handleBonusChange}
                    >
                        <option value="">{window.charPlannerData.i18n['No bonus'] || 'No bonus'}</option>
                        {calculatedStats && calculatedStats.isWeapon && <option value="damage">+{window.charPlannerData.i18n['Damage']}</option>}
                        {calculatedStats && calculatedStats.isArmor && <option value="armor">+{window.charPlannerData.i18n['Armor']}</option>}
                        <option value="strength">+{window.charPlannerData.i18n['Strength']}</option>
                        <option value="dexterity">+{window.charPlannerData.i18n['Dexterity']}</option>
                        <option value="agility">+{window.charPlannerData.i18n['Agility']}</option>
                        <option value="constitution">+{window.charPlannerData.i18n['Constitution']}</option>
                        <option value="charisma">+{window.charPlannerData.i18n['Charisma']}</option>
                        <option value="intelligence">+{window.charPlannerData.i18n['Intelligence']}</option>
                    </select>
                    <input 
                        type="number" 
                        className="w-1/3 bg-gray-200 p-1" 
                        min="0" 
                        value={localItem.bonusValue}
                        onChange={handleBonusValueChange}
                    />
                </div>

                <button 
                    className="bg-gray-200 px-4 py-2 rounded-md mt-2 sm:mt-0"
                    onClick={handleSave}
                >
                    {window.charPlannerData.i18n['Save']}
                </button>
                <button 
                    className="bg-gray-200 px-4 py-2 rounded-md mt-2 sm:mt-0" 
                    onClick={handleCancel}
                >
                    {window.charPlannerData.i18n['Cancel']}
                </button>
            </div>
            {localItem.base && (
                <>
                    {calculatedStats && calculatedStats.isWeapon && (
                        <div>
                            {window.charPlannerData.i18n['Damage']}
                            {Math.floor(calculatedStats.damageLow) + "-" + Math.floor(calculatedStats.damageHigh)}
                        </div>
                    )}
                    {Object.entries(statsColumnsMapping).map(([k, v]) => (
                        localItem.base && calculatedStats && calculatedStats.stats && calculatedStats.stats[v] !== 0 && k !== 'name' && (
                            <div key={k}>
                                {statAsString(calculatedStats.stats[v], k, 1, window.charPlannerData.i18n[`stat-${stripPercentageFromName(k)}`])}
                            </div>
                        )
                    ))}
                </>
            )}
        </div>
    );
});

ItemEditor.displayName = 'ItemEditor';

export default ItemEditor; 